import { FC, useState, useCallback, useEffect, useMemo } from 'react';
import { Box, Grid, CircularProgress, Alert, Typography, Select, MenuItem, FormControl, Button } from '@mui/material';
import { useSearchParams } from 'react-router-dom';

import { Sidebar } from '../../../common/components/Sidebar';
import SectionBar from '../../../common/components/SectionBar/SectionBar';
import { StatusTable } from '@/common/components/StatusTable/StatusTable.tsx';
import { NotificationSnackbar } from '../../../common/components/Snackbar';
import { useNavItems, usePermission } from '@/hooks';
import { useUser } from '@/hooks/useUser.ts';
import { useENO1Status } from './useENO1Status';
import { useModuleOperators } from '@/hooks';
import { StyledSegmentedControl, StyledSegmentButton } from '@/common/components/SectionBar';

import { StyledContainer, MainContent, TableSection } from './eNO1Status.styled';
// PLAYGROUND_PPO removed — not needed here

const ENO1Status: FC = () => {
  const [sidebarOpen, setSidebarOpen] = useState<boolean>(true);
  const [searchParams, setSearchParams] = useSearchParams();
  const navItems = useNavItems();
  const { hasPermission, isLoading: permissionLoading } = usePermission('/eno1', '');
  const { data: userData, isLoading: userLoading } = useUser();

  // Local filter state for form inputs (before submit)
  const [selectedYear, setSelectedYear] = useState<string>('');
  const [selectedPpo, setSelectedPpo] = useState<string>('');
  const [selectedDirection, setSelectedDirection] = useState<string>('all');
  const [selectedStatus, setSelectedStatus] = useState<string>('all');
  const [hasSubmitted, setHasSubmitted] = useState<boolean>(false);

  // Section bar form state (separate from API filters)
  const [sectionBarYear, setSectionBarYear] = useState<string>('');
  const [sectionBarPpo, setSectionBarPpo] = useState<string>('');
  const [sectionBarDirection, setSectionBarDirection] = useState<string>('all');
  const [sectionBarStatus, setSectionBarStatus] = useState<string>('all');

  // Get effective operators + cascading logic for eno1 module
  const {
    operators,
    availableYears,
    operatorsForYear,
    relationsData,
    isPpoLoading: isPposLoading,
    ppoError,
  } = useModuleOperators('eno1', { selectedYear, selectedPpo });

  // Convert years to strings for compatibility
  const years = availableYears.map(year => year.toString());

  // State for filters from URL parameters
  const [yearFilter, setYearFilter] = useState<string | undefined>(undefined);
  const [ppoFilter, setPpoFilter] = useState<string | undefined>(undefined);
  const [inboundFilter, setInboundFilter] = useState<boolean | undefined>(undefined);
  const [openFilter, setOpenFilter] = useState<boolean | undefined>(undefined);
  const [requestTimestamp, setRequestTimestamp] = useState<number>(Date.now());

  // Wrapper function for setSectionBarStatus
  const setSectionBarStatusWithDebug = useCallback((status: string) => {
    setSectionBarStatus(status);
  }, [sectionBarStatus]);

  // Initialize filters from URL parameters on component mount
  useEffect(() => {
    const yearFromUrl = searchParams.get('year');
    const ppoFromUrl = searchParams.get('ppo');
    const inboundFromUrl = searchParams.get('inbound');
    const openFromUrl = searchParams.get('open');

    if (yearFromUrl || ppoFromUrl || inboundFromUrl !== null || openFromUrl !== null) {
      setYearFilter(yearFromUrl || undefined);
      setPpoFilter(ppoFromUrl || undefined);
      setInboundFilter(inboundFromUrl !== null ? inboundFromUrl === 'true' : undefined);
      setOpenFilter(openFromUrl !== null ? openFromUrl === 'true' : undefined);
      setHasSubmitted(true);

      // Set form values from URL
      setSelectedYear(yearFromUrl || '');
      setSelectedPpo(ppoFromUrl || '');
      
      // Set direction from URL
      if (inboundFromUrl === 'true') setSelectedDirection('inbound');
      else if (inboundFromUrl === 'false') setSelectedDirection('outbound');
      else setSelectedDirection('all');

      // Set status from URL
      if (openFromUrl === 'true') setSelectedStatus('open');
      else if (openFromUrl === 'false') setSelectedStatus('closed');
      else setSelectedStatus('all');

      // Set section bar values
      setSectionBarYear(yearFromUrl || '');
      setSectionBarPpo(ppoFromUrl || '');
      if (inboundFromUrl === 'true') setSectionBarDirection('inbound');
      else if (inboundFromUrl === 'false') setSectionBarDirection('outbound');
      else setSectionBarDirection('all');
      if (openFromUrl === 'true') setSectionBarStatus('open');
      else if (openFromUrl === 'false') setSectionBarStatus('closed');
      else setSectionBarStatus('all');
    }
  }, [searchParams]);

  // Initialize section bar state when hasSubmitted becomes true
  useEffect(() => {
    if (hasSubmitted) {
      setSectionBarYear(selectedYear);
      setSectionBarPpo(selectedPpo);
      setSectionBarDirection(selectedDirection);
      setSectionBarStatus(selectedStatus);
    }
  }, [hasSubmitted, selectedYear, selectedPpo, selectedDirection, selectedStatus]);

  // Function to update URL parameters
  const updateUrlParams = useCallback((year?: string, ppo?: string, inbound?: boolean, open?: boolean) => {
    const newSearchParams = new URLSearchParams(searchParams);
    
    if (year) {
      newSearchParams.set('year', year);
    } else {
      newSearchParams.delete('year');
    }
    
    if (ppo) {
      newSearchParams.set('ppo', ppo);
    } else {
      newSearchParams.delete('ppo');
    }
    
    if (inbound !== undefined) {
      newSearchParams.set('inbound', inbound.toString());
    } else {
      newSearchParams.delete('inbound');
    }
    
    if (open !== undefined) {
      newSearchParams.set('open', open.toString());
    } else {
      newSearchParams.delete('open');
    }
    
    setSearchParams(newSearchParams);
  }, [searchParams, setSearchParams]);

  // Handle PPO selection change — years are independent of the selected DO
  const handlePpoChange = (value: string) => {
    setSelectedPpo(value);
  };

  // Handle year selection change with cascading behavior
  const handleYearChange = (value: string) => {
    // Only clear PPO selection if the currently selected PPO is not available for the new year
    if (selectedYear !== value && selectedPpo) {
      // Check if the current PPO is available for the new year by looking at the relations data
      const newYearKey = value;
      const relationsForNewYear = relationsData?.byYear?.[newYearKey];
      const isCurrentPPOAvailableForNewYear = relationsForNewYear?.some(relation => 
        relation.targetDO === selectedPpo &&
        relation.modules.some(module => 
          module.name === 'eno1' && module.enabled === true
        )
      );
      
      if (!isCurrentPPOAvailableForNewYear) {
        setSelectedPpo('');
      }
    }
    setSelectedYear(value);
  };

  // Filter handling functions
  const handleSubmit = () => {
    // Convert form values to API parameters
    const year = selectedYear || undefined;
    const ppo = selectedPpo || undefined;
    
    let inbound: boolean | undefined = undefined;
    if (selectedDirection === 'inbound') inbound = true;
    else if (selectedDirection === 'outbound') inbound = false;
    
    let open: boolean | undefined = undefined;
    if (selectedStatus === 'open') open = true;
    else if (selectedStatus === 'closed') open = false;

    // Update filters which will trigger API call
    setYearFilter(year);
    setPpoFilter(ppo);
    setInboundFilter(inbound);
    setOpenFilter(open);
    setHasSubmitted(true);
    
    // Update URL parameters
    updateUrlParams(year, ppo, inbound, open);
    
    // Set new timestamp to force cache invalidation and new request
    setRequestTimestamp(Date.now());
  };

  // Handle SectionBar PPO change — years are independent of the selected DO
  const handleSectionBarPpoChange = (value: string) => {
    setSectionBarPpo(value);
  };

  // Handle SectionBar year change with cascading behavior
  const handleSectionBarYearChange = (value: string) => {
    // Only clear PPO selection if the currently selected PPO is not available for the new year
    if (sectionBarYear !== value && sectionBarPpo) {
      // Check if the current PPO is available for the new year by looking at the relations data
      const newYearKey = value;
      const relationsForNewYear = relationsData?.byYear?.[newYearKey];
      const isCurrentPPOAvailableForNewYear = relationsForNewYear?.some(relation => 
        relation.targetDO === sectionBarPpo &&
        relation.modules.some(module => 
          module.name === 'eno1' && module.enabled === true
        )
      );
      
      if (!isCurrentPPOAvailableForNewYear) {
        setSectionBarPpo('');
      }
    }
    setSectionBarYear(value);
  };

  const handleSectionBarSubmit = (currentStatus?: string) => {
    const statusToUse = currentStatus || sectionBarStatus;
    
    // Convert section bar form values to API parameters
    const year = sectionBarYear || undefined;
    const ppo = sectionBarPpo || undefined;
    
    let inbound: boolean | undefined = undefined;
    if (sectionBarDirection === 'inbound') inbound = true;
    else if (sectionBarDirection === 'outbound') inbound = false;
    
    let open: boolean | undefined = undefined;
    if (statusToUse === 'open') open = true;
    else if (statusToUse === 'closed') open = false;

    // Update filters which will trigger API call
    setYearFilter(year);
    setPpoFilter(ppo);
    setInboundFilter(inbound);
    setOpenFilter(open);
    
    // Update URL parameters
    updateUrlParams(year, ppo, inbound, open);
    
    // Set new timestamp to force cache invalidation and new request
    setRequestTimestamp(Date.now());
  };

  const handleReset = () => {
    setSelectedYear('');
    setSelectedPpo('');
    setSelectedDirection('all');
    setSelectedStatus('all');
    setYearFilter(undefined);
    setPpoFilter(undefined);
    setInboundFilter(undefined);
    setOpenFilter(undefined);
    setHasSubmitted(false);
    
    // Clear URL parameters
    setSearchParams(new URLSearchParams());
  };

  const handleSectionBarReset = () => {
    setSectionBarYear('');
    setSectionBarPpo('');
    setSectionBarDirection('all');
    setSectionBarStatus('all');
    setYearFilter(undefined);
    setPpoFilter(undefined);
    setInboundFilter(undefined);
    setOpenFilter(undefined);
    
    // Clear URL parameters
    setSearchParams(new URLSearchParams());
    
    refetch();
  };

  // Use the ENO1Status hook with the filters
  const { 
    data: statusResponseData, 
    isLoading: statusLoading, 
    isError: statusError, 
    error: statusErrorDetails,
    refetch 
  } = useENO1Status({
    year: yearFilter,
    ppo: ppoFilter,
    inbound: inboundFilter,
    open: openFilter,
    timestamp: requestTimestamp
  });


  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error' | 'info';
  }>({
    open: false,
    message: '',
    severity: 'success',
  });

  const handleSnackbarClose = () => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  const handleSidebarToggle = () => {
    setSidebarOpen((prev) => !prev);
  };

  // Show loading indicator
  const showLoading = statusLoading || permissionLoading || userLoading;

  // Create custom filters for SectionBar when results are shown
  const customFiltersForSectionBar = useMemo(() => {
    if (!hasSubmitted) return undefined;

    return {
      eno1StatusPpo: sectionBarPpo,
      eno1StatusYear: sectionBarYear,
      eno1StatusDirection: sectionBarDirection,
      eno1StatusStatus: sectionBarStatus,
      eno1StatusPpoOptions: (sectionBarYear ? 
        relationsData?.byYear?.[sectionBarYear]?.filter(relation => 
          operators.includes(relation.targetDO) &&
          relation.modules.some(module => 
            module.name === 'eno1' && module.enabled === true
          )
        ).map(relation => ({
          id: relation.targetDO,
          name: relation.targetDO,
          disabled: false
        })) : operators.map(targetDO => ({
          id: targetDO,
          name: targetDO,
          disabled: false
        }))) || [],
      eno1StatusYearOptions: years,
      eno1StatusIsPpoLoading: isPposLoading,
      onEno1StatusPpoChange: handleSectionBarPpoChange,
      onEno1StatusYearChange: handleSectionBarYearChange,
      onEno1StatusDirectionChange: setSectionBarDirection,
      onEno1StatusStatusChange: setSectionBarStatusWithDebug,
      onEno1StatusSubmit: (currentStatus?: string) => handleSectionBarSubmit(currentStatus),
      onEno1StatusReset: handleSectionBarReset,
      eno1StatusIsSubmitting: statusLoading,
    };
  }, [
    hasSubmitted,
    sectionBarPpo,
    sectionBarYear,
    sectionBarDirection,
    sectionBarStatus,
    relationsData,
    operators,
    years,
    isPposLoading,
    handleSectionBarSubmit,
    handleSectionBarReset,
    statusLoading,
    setSectionBarStatusWithDebug,
  ]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column' }} data-testid='eno1-status-container'>
      <Grid container>
        {/* First Row - SectionBar */}
        <Grid item xs={12}>
          <SectionBar
            title='eNO1 Status'
            subtitle='eNO1 Sampling'
            onToggleSidebar={handleSidebarToggle}
            isSidebarOpen={sidebarOpen}
            showFilters={{
              customENO1Status: !!hasSubmitted,
            }}
            customFilters={customFiltersForSectionBar}
            data-testid="section-bar"
          />
        </Grid>

        {/* Second Row - Sidebar and MainContent */}
        <Grid item xs={12} sx={{ display: 'flex', position: 'relative' }}>
          {/* Sidebar */}
          {sidebarOpen && (
            <Box sx={{ position: 'relative' }}>
              <Sidebar isOpen={sidebarOpen} onToggle={handleSidebarToggle} navItems={navItems} data-testid="eno1-status-sidebar" />
            </Box>
          )}

          {/* Main Content */}
          <MainContent sidebarOpen={sidebarOpen} data-testid='eno1-status-main-content'>
            {showLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }} data-testid="loading-indicator">
                <CircularProgress />
              </Box>
            ) : statusError ? (
              <Alert severity='error' data-testid="error-alert">
                Error loading data: {statusErrorDetails instanceof Error ? statusErrorDetails.message : 'Unknown error'}
              </Alert>
            ) : !hasPermission ? (
              <Alert severity='warning' data-testid="permission-error-alert">You don't have permission to view this section</Alert>
            ) : (
              <>
                {/* Filter Section - Hide when results are shown */}
                {!hasSubmitted && (
                  <Box sx={{ p: 3, backgroundColor: '#FFFFFF', borderRadius: 1, mb: 3, maxWidth: '100%' }}>
                    <Grid container spacing={2} p="40px">
                      {/* PPO Dropdown - Full width */}
                      <Typography variant="h3" sx={{ 
                        mb: 1, 
                        mt: 1,
                        ml: 2,
                        fontWeight: 700,
                        fontSize: '24px',
                        lineHeight: '100%',
                        letterSpacing: '0%',
                        color: '#063142'
                      }}>
                        eNO1 Status
                      </Typography>
                      <Grid item xs={12}>
                        <Typography variant="body2" sx={{ mb: 1, fontWeight: 600, color: '#333' }}>
                          DO
                        </Typography>
                        <FormControl fullWidth size="small">
                          <Select
                            value={selectedPpo}
                            onChange={(e) => handlePpoChange(e.target.value)}
                            displayEmpty
                            disabled={isPposLoading}
                            sx={{ 
                              backgroundColor: 'white',
                              maxWidth: '200px',
                              '& .MuiSelect-select': {
                                padding: '12px 14px',
                              }
                            }}
                          >
                            <MenuItem value="">ALL</MenuItem>
                            {isPposLoading ? (
                              <MenuItem disabled>Loading...</MenuItem>
                            ) : (
                              (selectedYear ? operatorsForYear : operators).map((targetDO) => (
                                <MenuItem key={targetDO} value={targetDO}>
                                  {targetDO}
                                </MenuItem>
                              ))
                            )}
                            {ppoError && <MenuItem disabled>Error loading PPOs</MenuItem>}
                          </Select>
                        </FormControl>
                      </Grid>

                      {/* Year Dropdown - Full width */}
                      <Grid item xs={12}>
                        <Typography variant="body2" sx={{ mb: 1, fontWeight: 600, color: '#333' }}>
                          Year
                        </Typography>
                        <FormControl fullWidth size="small">
                          <Select
                            value={selectedYear}
                            onChange={(e) => handleYearChange(e.target.value)}
                            displayEmpty
                            sx={{ 
                              backgroundColor: 'white',
                              maxWidth: '200px',
                              '& .MuiSelect-select': {
                                padding: '12px 14px',
                              }
                            }}
                          >
                            <MenuItem value="">ALL</MenuItem>
                            {years.map((year) => (
                              <MenuItem key={year} value={year}>
                                {year}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Grid>

                      {/* Inbound/Outbound Filter */}
                      <Grid item xs="auto">
                        <StyledSegmentedControl sx={{ mb: 2, width: '208px' }}>
                          <StyledSegmentButton
                            active={selectedDirection === 'all'}
                            onClick={() => setSelectedDirection('all')}
                            sx={{ color: selectedDirection === 'all' ? '#ffffff' : '#00B6BA' }}
                          >
                            All
                          </StyledSegmentButton>
                          <StyledSegmentButton
                            active={selectedDirection === 'inbound'}
                            onClick={() => setSelectedDirection('inbound')}
                            sx={{ color: selectedDirection === 'inbound' ? '#ffffff' : '#00B6BA' }}
                          >
                            Inbound
                          </StyledSegmentButton>
                          <StyledSegmentButton
                            active={selectedDirection === 'outbound'}
                            onClick={() => setSelectedDirection('outbound')}
                            sx={{ color: selectedDirection === 'outbound' ? '#ffffff' : '#00B6BA' }}
                          >
                            Outbound
                          </StyledSegmentButton>
                        </StyledSegmentedControl>
                      </Grid>

                      {/* Open/Closed Filter */}
                      <Grid item xs="auto">
                        <StyledSegmentedControl sx={{ mb: 3, width: '168px' }}>
                          <StyledSegmentButton
                            active={selectedStatus === 'all'}
                            onClick={() => setSelectedStatus('all')}
                            sx={{ color: selectedStatus === 'all' ? '#ffffff' : '#00B6BA' }}
                          >
                            All
                          </StyledSegmentButton>
                          <StyledSegmentButton
                            active={selectedStatus === 'open'}
                            onClick={() => setSelectedStatus('open')}
                            sx={{ color: selectedStatus === 'open' ? '#ffffff' : '#00B6BA' }}
                          >
                            Open
                          </StyledSegmentButton>
                          <StyledSegmentButton
                            active={selectedStatus === 'closed'}
                            onClick={() => setSelectedStatus('closed')}
                            sx={{ color: selectedStatus === 'closed' ? '#ffffff' : '#00B6BA' }}
                          >
                            Closed
                          </StyledSegmentButton>
                        </StyledSegmentedControl>
                      </Grid>

                      {/* Action Buttons */}
                      <Grid item xs={12}>
                        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                          <Button
                            variant="outlined"
                            onClick={handleReset}
                            sx={{
                              minWidth: '100px',
                              borderRadius: '1000px',
                              height: '40px',
                              borderColor: '#dee2e6',
                              color: '#6c757d',
                              backgroundColor: '#f8f9fa',
                              fontWeight: 600,
                              textTransform: 'uppercase',
                              '&:hover': {
                                borderColor: '#adb5bd',
                                backgroundColor: '#e9ecef'
                              }
                            }}
                          >
                            RESET
                          </Button>
                          <Button
                            variant="contained"
                            onClick={handleSubmit}
                            sx={{
                              minWidth: '100px',
                              borderRadius: '1000px',
                              height: '40px',
                              backgroundColor: '#6c757d',
                              color: 'white',
                              fontWeight: 600,
                              textTransform: 'uppercase',
                              '&:hover': {
                                backgroundColor: '#5a6268'
                              }
                            }}
                          >
                            SUBMIT
                          </Button>
                        </Box>
                      </Grid>
                    </Grid>
                  </Box>
                )}



                                {/* Status Tables - Only show after submit */}
                {hasSubmitted && (
                  <StyledContainer data-testid="tables-container">
                    {/* Show Inbound Table only when direction is 'all' or 'inbound' */}
                    {(inboundFilter === undefined || inboundFilter === true) && (
                      <TableSection data-testid="inbound-status-section" sx={{ mb: inboundFilter === undefined ? 0 : undefined }}>
                        <StatusTable 
                          data={statusResponseData?.inbound || []}
                          title={`Inbound from * to ${userData?.designatedOperator}`}
                          showQuarters={['q1', 'q2', 'q3', 'q4']}
                          backgroundColor="#F0FFF0"
                        />
                      </TableSection>
                    )}
                    
                    {/* Show Outbound Table only when direction is 'all' or 'outbound' */}
                    {(inboundFilter === undefined || inboundFilter === false) && (
                      <TableSection data-testid="outbound-status-section">
                        <StatusTable 
                          data={statusResponseData?.outbound || []}
                          title={`Outbound from ${userData?.designatedOperator} to *`}
                          showQuarters={['q1', 'q2', 'q3', 'q4']}
                          backgroundColor="#FFF0F0"
                        />
                      </TableSection>
                    )}
                  </StyledContainer>
                )}
              </>
            )}

            {/* Notification Snackbar */}
            <NotificationSnackbar
              open={snackbar.open}
              message={snackbar.message}
              severity={snackbar.severity}
              onClose={handleSnackbarClose}
              data-testid="notification-snackbar"
            />
          </MainContent>
        </Grid>
      </Grid>
    </Box>
  );
};

export default ENO1Status;
